import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';

/* FETCH FIELD OFFICER INFO */
export const fetchFieldOfficerInfo = createAsyncThunk(
  'fieldOfficer/fetchInfo',
  async ({ id }, { rejectWithValue }) => {
    try {
      const role = getRole();

      const url =
        role === 'SUPPORT'
          ? `/super-admin/field-officer/${id}`
          : `/admin/field-officers`;

      const { data } = await axiosInstance.get(url);

      if (role === 'PARTNER' || role === 'TEAM_MEMBER') {
        const officer = data.find((fo) => fo.field_officer_id === id);
        return officer;
      }

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg);
    }
  }
);

// FETCH FIELD OFFICER FARMERS (SUPPORT)
export const fetchFieldOfficerFarmers = createAsyncThunk(
  'fieldOfficer/fetchFarmers',
  async ({ id, search = '', page, limit }, { rejectWithValue, signal }) => {
    try {
      const params = {};
      if (search) params.search = search;
      if (page) params.page = page;
      if (limit) params.limit = limit;

      const { data } = await axiosInstance.get(
        `/super-admin/field-officer/${id}/farmers`,
        { params, signal }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg);
    }
  }
);

/* UPDATE FIELD OFFICER (ROLE BASED) */
export const updateFieldOfficerInfo = createAsyncThunk(
  'fieldOfficer/update',
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const role = getRole();

      const url =
        role === 'SUPPORT'
          ? `/super-admin/field-officer/${id}`
          : `/admin/update-field-officer/${id}`;

      const { data } = await axiosInstance.put(url, values);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg);
    }
  }
);

/* FETCH FIELD OFFICER ANALYTICS (PARTNER) */
export const fetchFieldOfficerAnalytics = createAsyncThunk(
  'fieldOfficer/fetchAnalytics',
  async ({ id }, { rejectWithValue }) => {
    try {
      const role = getRole();

      if (role !== 'PARTNER' && role !== 'TEAM_MEMBER') {
        return {
          reportCount: 0,
          sampleCount: 0,
        };
      }

      const { data } = await axiosInstance.get(
        `/admin/field-officer-analytics/${id}`
      );

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg);
    }
  }
);



/* SLICE */
const initialState = {
  fieldOfficerInfo: {},
  farmers: [],
  status: 'idle',
  error: null,

  farmersStatus: 'idle',
  farmersError: null,

  foUpdateStatus: 'idle',
  foUpdateError: null,

  analytics: {
    reportCount: 0,
    sampleCount: 0,
  },
  analyticsStatus: 'idle',
  analyticsError: null,
};


const fieldOfficerSlice = createSlice({
  name: 'fieldOfficer',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* FETCH INFO */
      .addCase(fetchFieldOfficerInfo.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchFieldOfficerInfo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.fieldOfficerInfo = action.payload;
      })
      .addCase(fetchFieldOfficerInfo.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      /* FETCH FARMERS */
      .addCase(fetchFieldOfficerFarmers.pending, (state) => {
        state.farmersStatus = 'loading';
        state.farmersError = null;
      })
      .addCase(fetchFieldOfficerFarmers.fulfilled, (state, action) => {
        state.farmersStatus = 'succeeded';
        state.farmers = action.payload.farmers;
      })
      .addCase(fetchFieldOfficerFarmers.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.farmersStatus = 'failed';
        state.farmersError = action.payload;
      })

      /* UPDATE */
      .addCase(updateFieldOfficerInfo.pending, (state) => {
        state.foUpdateStatus = 'loading';
      })
      .addCase(updateFieldOfficerInfo.fulfilled, (state) => {
        state.foUpdateStatus = 'succeeded';
      })
      .addCase(updateFieldOfficerInfo.rejected, (state, action) => {
        state.foUpdateStatus = 'failed';
        state.foUpdateError = action.payload;
      })

      /* FETCH ANALYTICS */
      .addCase(fetchFieldOfficerAnalytics.pending, (state) => {
        state.analyticsStatus = 'loading';
      })
      .addCase(fetchFieldOfficerAnalytics.fulfilled, (state, action) => {
        state.analyticsStatus = 'succeeded';
        state.analytics = action.payload;
      })
      .addCase(fetchFieldOfficerAnalytics.rejected, (state, action) => {
        state.analyticsStatus = 'failed';
        state.analyticsError = action.payload;
      });
  },
});

export default fieldOfficerSlice.reducer;